"use client";
import * as React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, Typography, Chip, IconButton, Collapse, Box, Link, Stack,
} from "@mui/material";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PortalScope } from "@/analytics/PortalScope";
import {
  getScheduleHistory, getJobEvents,
  type ScheduleHistoryItem, type JobEvent,
} from "../../api/schedules";

interface Props {
  scheduleId: string;
  scheduleName: string;
  open: boolean;
  onClose: () => void;
}

type Status = ScheduleHistoryItem["status"];

function statusColor(status: Status): "default" | "success" | "error" | "warning" | "info" {
  switch (status) {
    case "succeeded": return "success";
    case "failed": return "error";
    case "cancelled":
    case "cancelling": return "warning";
    case "running":
    case "awaiting_approval": return "info";
    default: return "default"; // pending
  }
}

/** Friendly label for an egpt_jobs status. */
function statusLabel(status: Status): string {
  return status === "succeeded" ? "Completed"
    : status === "awaiting_approval" ? "Needs approval"
    : status.charAt(0).toUpperCase() + status.slice(1);
}

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "—");

/** One run row, expandable to its step-by-step event timeline. */
function JobRow({ job, onOpenChat }: { job: ScheduleHistoryItem; onOpenChat: (sid: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [events, setEvents] = React.useState<JobEvent[] | null>(null);
  const [loading, setLoading] = React.useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && events === null && !loading) {
      setLoading(true);
      try {
        setEvents(await getJobEvents(job.id));
      } catch {
        setEvents([]); // soft-fail: show "no steps"
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: open ? "unset" : undefined } }}>
        <TableCell sx={{ width: 36, px: 0.5 }}>
          <IconButton size="small" data-ph={open ? "collapse" : "expand"} onClick={toggle} aria-label={open ? "collapse" : "expand steps"}>
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip label={statusLabel(job.status)} size="small" color={statusColor(job.status)} />
        </TableCell>
        <TableCell>{fmt(job.started_at)}</TableCell>
        <TableCell>{fmt(job.completed_at)}</TableCell>
        <TableCell>
          {job.error ? (
            <Typography variant="body2" color="error">{job.error}</Typography>
          ) : job.result ? (
            <Typography variant="body2">{job.result}</Typography>
          ) : "—"}
          {/* Links to where the run's output lives, when present. */}
          <Stack direction="row" spacing={1.5} sx={{ mt: job.session_id || job.artifact_id ? 0.5 : 0 }}>
            {job.session_id && (
              <Link
                component="button"
                type="button"
                variant="caption"
                data-ph="open_chat"
                onClick={() => onOpenChat(job.session_id!)}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
              >
                <ExternalLink size={12} /> Open chat
              </Link>
            )}
            {job.artifact_id && (
              <Typography variant="caption" color="text.secondary">
                Artifact: {job.artifact_id.slice(0, 8)}
              </Typography>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1, pl: 5 }}>
              {loading && <CircularProgress size={16} />}
              {!loading && events !== null && events.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No step detail recorded for this run.
                </Typography>
              )}
              {!loading && events && events.length > 0 && (
                <Stack spacing={0.5}>
                  {events.map((ev) => (
                    <Stack key={ev.seq} direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
                      <Chip label={ev.kind} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, wordBreak: "break-word" }}>
                        {stepSummary(ev)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(ev.ts).toLocaleTimeString()}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/** A compact one-line description of a job event for the timeline. */
function stepSummary(ev: JobEvent): string {
  const d = ev.data ?? {};
  switch (ev.kind) {
    case "start": return String(d.prompt_preview ?? d.name ?? "Started");
    case "tool_use": return `Used tool: ${String(d.name ?? d.tool ?? "?")}`;
    case "tool_result": return d.error ? `Tool error: ${String(d.error)}` : "Tool result";
    case "thinking": return String(d.text ?? "Thinking…").slice(0, 160);
    case "done": return String(d.text ?? "Done").slice(0, 160);
    case "error": return `Error: ${String(d.reason ?? d.error ?? "unknown")}`;
    default: {
      const s = JSON.stringify(d);
      return s.length > 160 ? s.slice(0, 160) + "…" : s;
    }
  }
}

export function ScheduleHistoryDialog({ scheduleId, scheduleName, open, onClose }: Props) {
  const navigate = useNavigate();
  const [jobs, setJobs] = React.useState<ScheduleHistoryItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setJobs(null);
    setError(null);
    getScheduleHistory(scheduleId)
      .then(setJobs)
      .catch((e) => setError(String(e)));
  }, [open, scheduleId]);

  const openChat = (sessionId: string) => {
    // The chat surface reads ?session= to load a conversation on the home route.
    navigate(`/?session=${encodeURIComponent(sessionId)}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Portaled, so it declares its path: a dialog is its own section. */}
      <PortalScope section="history_dialog">
        <DialogTitle>History — {scheduleName}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && jobs === null && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CircularProgress size={24} />
            </div>
          )}
          {jobs !== null && jobs.length === 0 && (
            <Typography color="text.secondary">No runs yet.</Typography>
          )}
          {jobs !== null && jobs.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 36, px: 0.5 }} />
                  <TableCell>Status</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <JobRow key={job.id} job={job} onOpenChat={openChat} />
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-ph="close" onClick={onClose}>Close</Button>
        </DialogActions>
      </PortalScope>
    </Dialog>
  );
}
